import { createContext, useContext, useEffect, useState } from "react";
import fr from "./locales/fr";
import en from "./locales/en";
import pt from "./locales/pt";
import es from "./locales/es";
import de from "./locales/de";

export const LANGS = ["fr", "en", "pt", "es", "de"];
const DICT = { fr, en, pt, es, de };
const LOCALE_TAG = { fr: "fr-FR", en: "en-GB", pt: "pt-PT", es: "es-ES", de: "de-DE" };
const I18nContext = createContext({ lang: "fr", t: (k) => fr.ui[k], setLang: () => {} });

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => (LANGS.includes(localStorage.getItem("rox_lang")) ? localStorage.getItem("rox_lang") : "fr"));
  const setLang = (l) => { localStorage.setItem("rox_lang", l); setLangState(l); document.documentElement.lang = l; };
  useEffect(() => { document.documentElement.lang = lang; }, [lang]);
  const t = (key, ...args) => {
    const v = DICT[lang].ui[key] ?? fr.ui[key] ?? key;
    return typeof v === "function" ? v(...args) : v;
  };
  const fmtDate = (iso, opts = { day: "numeric", month: "long", year: "numeric" }) => (iso ? new Date(iso).toLocaleDateString(LOCALE_TAG[lang], opts) : "");
  return <I18nContext.Provider value={{ lang, t, setLang, fmtDate }}>{children}</I18nContext.Provider>;
}

export const useT = () => useContext(I18nContext);

export const LangToggle = ({ className = "" }) => {
  const { lang, setLang } = useT();
  return (
    <div className={`flex overflow-hidden rounded-[3px] border border-rox-line text-[11px] font-semibold uppercase tracking-wider ${className}`} data-testid="lang-toggle">
      {LANGS.map((l) => (
        <button key={l} data-testid={`lang-${l}`} data-active={lang === l} onClick={() => setLang(l)}
          className={`px-2 py-1 transition-[background-color,color] ${lang === l ? "bg-rox-off text-rox-black" : "text-rox-muted hover:text-rox-off"}`}>
          {l}
        </button>
      ))}
    </div>
  );
};

export const useLabels = () => {
  const { lang } = useT();
  const L = DICT[lang].labels;
  const pick = (kind, id) => L[kind]?.[id] ?? fr.labels[kind]?.[id] ?? id;
  return {
    level: (p) => pick("level", p.level),
    levelSub: (id) => pick("level_sub", id),
    category: (p) => pick("category", p.category),
    categoryShort: (id) => pick("category_short", id),
    goal: (p) => pick("goal", p.goal),
    goalShort: (p) => pick("goal_short", p.goal),
    availability: (p) => (p.availability || []).map((a) => pick("availability", a)),
    availabilityLabel: (id) => pick("availability", id),
    station: (key) => pick("station", key),
    format: (id) => pick("format", id),
    weight: (key) => pick("weights", key),
    status: (id) => DICT[lang].ui[`status_${id}`] ?? fr.ui[`status_${id}`],
    reasons: (p) => (p.reason_codes || []).map((r) => {
      const fn = L.reasons[r.code] || fr.labels.reasons[r.code];
      const arg = r.code === "same_format" ? pick("category", r.arg) : r.code === "complementary" ? (r.arg || []).map((s) => pick("station", s)).join(" & ") : r.arg;
      return fn ? fn(arg) : r.code;
    }),
    strengths: (p) => L.strengths((p.strong_stations || []).map((k) => pick("station", k))),
  };
};
